import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, PlusCircle, Pencil, Eye, Copy, Pause, Send, Trash2, Play, Info, Layers, PlayCircle, Calendar, MessageSquareWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  COURSE_CATEGORIES,
  CURSOS_EJEMPLO,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetCursos, apiAccionCurso, apiEliminarCurso } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { EstadoBadge, COURSE_NOTICE } from "./mktui";

const catLabel = (id) => COURSE_CATEGORIES.find((c) => c.id === id)?.label || id;
const countModulos = (c) => (typeof c.modulos === "number" ? c.modulos : (c.modulos?.length || 0));
const countLecciones = (c) =>
  typeof c.lecciones === "number" ? c.lecciones : (c.modulos || []).reduce((n, m) => n + (m.lecciones?.length || 0), 0);

function CursoCard({ c, onAccion, ejemplo }) {
  const acciones = [
    { key: "editar", label: "Editar", Icon: Pencil },
    { key: "preview", label: "Vista previa", Icon: Eye },
    { key: "duplicar", label: "Duplicar", Icon: Copy },
    c.estado === "pausado" ? { key: "reanudar", label: "Reanudar", Icon: Play } : { key: "pausar", label: "Pausar", Icon: Pause },
    { key: "revision", label: "Enviar a revisión", Icon: Send },
    { key: "eliminar", label: "Eliminar", Icon: Trash2, danger: true },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden flex flex-col" data-testid={`curso-card-${c.id}`}>
      <div className="h-28 relative flex items-center justify-center" style={{ background: c.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
        {c.cover ? <img src={c.cover} alt={c.titulo} className="w-full h-full object-cover" /> : <GraduationCap size={30} style={{ color: MKT_GOLD }} />}
        <span className="absolute top-2 left-2"><EstadoBadge estado={c.estado} testid={`curso-estado-${c.id}`} /></span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: MKT_GOLD }}>{catLabel(c.categoria)}</p>
        <p className="mt-1 font-bold text-slate-900 text-sm leading-tight">{c.titulo}</p>
        <p className="text-xs text-slate-500 mt-0.5">{c.instructor}</p>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Layers size={12} /> {countModulos(c)} módulos</span>
          <span className="inline-flex items-center gap-1"><PlayCircle size={12} /> {countLecciones(c)} lecciones</span>
        </div>
        <p className="mt-2 font-extrabold text-slate-900">{Number(c.precio || 0) === 0 ? "Gratis" : `S/ ${Number(c.precio).toFixed(2)}`}</p>
        {c.updatedAt && <p className="mt-1 text-[11px] text-slate-400 inline-flex items-center gap-1"><Calendar size={11} /> Actualizado: {c.updatedAt}</p>}
        <p className="mt-2 text-[11px] text-slate-400 flex items-start gap-1"><Info size={11} className="mt-0.5 flex-shrink-0" /> {COURSE_NOTICE}</p>

        {c.comentarios?.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid={`curso-comentarios-${c.id}`}>
            <p className="text-[11px] font-bold text-amber-800 inline-flex items-center gap-1"><MessageSquareWarning size={12} /> Comentarios de revisión</p>
            <ul className="mt-1 space-y-1">
              {c.comentarios.map((cm, i) => (
                <li key={i} className="text-[11px] text-amber-700">• {cm.texto}</li>
              ))}
            </ul>
          </div>
        )}

        {!ejemplo && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-1.5">
            {acciones.map((a) => (
              <button
                key={a.key}
                onClick={() => onAccion(a.key, c)}
                data-testid={`curso-accion-${a.key}-${c.id}`}
                title={a.label}
                className={`inline-flex flex-col items-center gap-0.5 py-2 rounded-lg text-[10px] font-semibold transition-colors ${a.danger ? "text-red-600 hover:bg-red-50" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}
              >
                <a.Icon size={15} /> {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MisCursos() {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    try {
      setCursos(await apiGetCursos());
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const onAccion = async (accion, c) => {
    if (accion === "editar" || accion === "preview") return navigate(`/marketplace/cursos/crear?id=${c.id}`);
    try {
      if (accion === "eliminar") {
        await apiEliminarCurso(c.id);
        toast.success("Curso eliminado.");
      } else {
        const endpoint = accion === "reanudar" ? "pausar" : accion === "revision" ? "enviar" : accion;
        await apiAccionCurso(c.id, endpoint);
        toast.success(
          accion === "duplicar" ? "Curso duplicado (borrador)." :
          accion === "pausar" ? "Curso pausado." :
          accion === "reanudar" ? "Curso reanudado." : "Curso enviado a revisión.",
        );
      }
      await cargar();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  if (cargando) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="cursos-loading"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="mis-cursos">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Mis Cursos</h2>
          <p className="text-sm text-slate-500">Crea y administra tus cursos como instructor independiente.</p>
        </div>
        <button onClick={() => navigate("/marketplace/cursos/crear")} data-testid="crear-curso-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
          <PlusCircle size={18} /> Crear curso
        </button>
      </div>

      <div className="flex items-start gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-3">
        <Info size={15} className="mt-0.5 flex-shrink-0" style={{ color: MKT_GOLD }} />
        {COURSE_NOTICE}
      </div>

      {cursos.length === 0 ? (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft p-10 text-center" data-testid="cursos-vacio">
            <span className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
              <GraduationCap size={26} />
            </span>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Aún no tienes cursos publicados.</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Crea tu primer curso independiente y compártelo con la comunidad de construcción.</p>
            <button onClick={() => navigate("/marketplace/cursos/crear")} data-testid="crear-curso-vacio-btn" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
              <PlusCircle size={16} /> Crear curso
            </button>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">Así se verán tus cursos</h3>
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Preliminar</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cursos-ejemplo">
              {CURSOS_EJEMPLO.map((c) => (
                <CursoCard key={c.id} c={c} ejemplo onAccion={() => {}} />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cursos-lista">
          {cursos.map((c) => (
            <CursoCard key={c.id} c={c} onAccion={onAccion} />
          ))}
        </div>
      )}
    </div>
  );
}
